/**
 * This file contains the logic for creating an idb connection and reading from it.
 *
 * Thew write path is intentionally split out as only the read path is needed for early
 * requesters.
 */

import { getGlobal, setGlobal } from './global';

// Disables the indexeddb write behavior to make testing less painful.
let __disableIdb = false;

/**
 * Re-enables indexeddb for testing this file
 */
export const __TEST_ONLY__REENABLE_IDB = () => {
  __disableIdb = false;
};

/**
 * Disables the IndexedDB integration. Only for use in tests!
 */
export const __TEST_ONLY__DISABLE_IDB = () => {
  __disableIdb = true;
};

export const CACHE_OBJECT_STORE = 'cache';
export const METRICS_OBJECT_STORE = 'metrics';

export type Store = typeof CACHE_OBJECT_STORE | typeof METRICS_OBJECT_STORE;

export type PortalUserResolver = () => Promise<{
  portalId: string | number;
  userId: string | number;
}>;

const open = ({
  resolvePortalAndUserId,
}: {
  resolvePortalAndUserId: PortalUserResolver;
}) =>
  resolvePortalAndUserId().then(
    ({ portalId, userId }) =>
      new Promise<IDBDatabase>((resolve, reject) => {
        try {
          const openRequest = window.indexedDB.open(
            `persist-promise/cache/${portalId}/${userId}`,
            1
          );

          if (openRequest) {
            openRequest.onerror = reject;

            openRequest.onupgradeneeded = () => {
              const result = openRequest.result;
              if (!result.objectStoreNames.contains(CACHE_OBJECT_STORE)) {
                result.createObjectStore(CACHE_OBJECT_STORE);
              }

              if (!result.objectStoreNames.contains(METRICS_OBJECT_STORE)) {
                result.createObjectStore(METRICS_OBJECT_STORE);
              }
            };

            openRequest.onsuccess = () => {
              // Set a global error handler on the db
              openRequest.result.onerror = console.error;

              resolve(openRequest.result);
            };
          }
        } catch (e) {
          reject(e);
        }
      })
  );

const idbConnectionKey = '__persistPromiseIDBConnection';

/**
 * Failure injection isn't honored by the persisted cache: on a failed fetch the
 * read path fault-tolerantly serves the stored copy, masking the injected
 * failure. When the HTTP_FAILURE_INJECTION flag is set, skip persistence so the
 * failure surfaces via hub-http instead. Mirrors quick-fetch's
 * shouldSkipEarlyRequestDueToFailureInjection -- no env gate, since injection is
 * a non-prod tool that isn't honored in production regardless.
 */
const shouldSkipPersistenceDueToFailureInjection = () => {
  try {
    return !!localStorage.getItem('HTTP_FAILURE_INJECTION');
  } catch (__err) {
    return false;
  }
};

export const openIndexedDBConnection = ({
  resolvePortalAndUserId,
}: {
  resolvePortalAndUserId: PortalUserResolver;
}) => {
  if (__disableIdb) {
    return Promise.reject(Error('IndexedDB is disabled'));
  }

  if (shouldSkipPersistenceDueToFailureInjection()) {
    return Promise.reject(
      Error('IndexedDB is disabled due to failure injection')
    );
  }

  const existingConn = getGlobal<Promise<IDBDatabase>>(idbConnectionKey);

  if (existingConn) {
    return existingConn;
  }

  const conn = open({ resolvePortalAndUserId });

  setGlobal(idbConnectionKey, conn);

  return conn;
};

/**
 *
 * @param key The key to query in indexeddb
 * @returns A promise that resolves to a value, or rejects if the value is null/undefined or some other error ocurred
 */
export const getFromIDB = <TResult>({
  key,
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId,
}: {
  key: string;
  store?: Store;
  resolvePortalAndUserId: PortalUserResolver;
}) =>
  openIndexedDBConnection({ resolvePortalAndUserId }).then(
    (db) =>
      new Promise<TResult>((resolve, reject) => {
        try {
          const request = db
            .transaction(store, 'readonly')
            .objectStore(store)
            .get(key);

          request.onerror = reject;
          request.onsuccess = () =>
            // NOTE: Load-bearing != (not !==)
            request.result != null
              ? resolve(request.result)
              : reject(Error('Result was empty'));
        } catch (e) {
          reject(e);
        }
      })
  );
